/**
 * My Flock — saved birds page.
 *
 * Free users: see localStorage birds (capped at 2) with a paywall CTA when full.
 * Pro users: see their MongoDB-backed birds (unlimited).
 */
import { useState } from "react";
import { useFlock, FREE_BIRD_LIMIT } from "@/lib/flock";
import { usePro } from "@/lib/pro";
import {
  defaultOptions,
  PHENOTYPES,
  findActiveOption,
  applyPhenotype,
  summarizePhenotype,
  LOCI_UI,
} from "@/lib/phenotypes";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, Plus, Trash2, Edit3, Bird, ArrowRight, Sparkles, ChevronDown, Heart } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import PairingsTab from "@/pages/PairingsTab";

function encodeParentOptions(options) {
  // Mirror the Calculator's encodeParent so links are URL-compatible.
  return Object.entries(options || {})
    .filter(([, v]) => v?.state && v.state !== "no")
    .map(([k, v]) => `${k}:${v.state}`)
    .join(",");
}

function MiniSwatch({ option }) {
  return (
    <div
      className="w-7 h-7 border border-border rounded-sm shrink-0"
      style={{ backgroundColor: option.swatch }}
      title={option.label}
    />
  );
}

function BirdCard({ bird, onEdit, onDelete }) {
  const navigate = useNavigate();
  const summary = summarizePhenotype({ sex: bird.sex, options: bird.options || {} });
  const accent = bird.sex === "female" ? "border-pink-500/40" : "border-primary/40";
  const sexBadge =
    bird.sex === "female"
      ? "bg-pink-500/10 text-pink-700"
      : "bg-primary/10 text-primary";

  const activeSwatches = PHENOTYPES.map((cat) => {
    const id = findActiveOption({ options: bird.options || {} }, cat);
    if (!id) return null;
    const opt = cat.options.find((o) => o.id === id);
    if (!opt) return null;
    if (opt.id.startsWith("no-") || /^standard$|^normal$/i.test(opt.label)) return null;
    return opt;
  }).filter(Boolean);

  const handleUseAs = (role) => {
    const params = new URLSearchParams();
    const encoded = encodeParentOptions(bird.options);
    params.set(role === "sire" ? "m" : "f", encoded);
    navigate(`/calculator?${params.toString()}`);
  };

  return (
    <div
      data-testid={`bird-card-${bird.id}`}
      className={`border ${accent} bg-card p-5 flex flex-col gap-3`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="overline">
            <span
              className={`inline-block px-1.5 py-0.5 rounded-sm font-mono ${sexBadge}`}
            >
              {bird.sex === "male" ? "Cock ♂" : "Hen ♀"}
            </span>
          </div>
          <div
            className="font-bold text-lg tracking-tight mt-1 truncate"
            data-testid={`bird-name-${bird.id}`}
          >
            {bird.name}
          </div>
          {bird.ring_number ? (
            <div
              className="font-mono text-[11px] text-muted-foreground mt-0.5"
              data-testid={`bird-ring-${bird.id}`}
            >
              ◯ {bird.ring_number}
            </div>
          ) : null}
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            data-testid={`bird-edit-${bird.id}`}
            onClick={() => onEdit(bird)}
            className="border border-border w-7 h-7 grid place-items-center rounded-sm hover:bg-secondary"
            title="Edit"
          >
            <Edit3 size={12} />
          </button>
          <button
            type="button"
            data-testid={`bird-delete-${bird.id}`}
            onClick={() => onDelete(bird)}
            className="border border-border w-7 h-7 grid place-items-center rounded-sm hover:bg-destructive hover:text-destructive-foreground"
            title="Delete"
          >
            <Trash2 size={12} />
          </button>
        </div>
      </div>

      <div className="text-xs text-muted-foreground leading-snug">{summary}</div>

      {activeSwatches.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {activeSwatches.map((o) => (
            <MiniSwatch key={`${o.id}-${o.label}`} option={o} />
          ))}
        </div>
      )}

      {bird.notes ? (
        <div className="text-[11px] text-muted-foreground italic border-l-2 border-border pl-2">
          {bird.notes}
        </div>
      ) : null}

      <div className="border-t border-border pt-3 flex gap-2 mt-auto">
        <button
          type="button"
          data-testid={`bird-use-sire-${bird.id}`}
          onClick={() => handleUseAs("sire")}
          disabled={bird.sex !== "male"}
          className="text-xs border border-border px-2.5 py-1.5 rounded-sm hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 flex-1 justify-center"
        >
          Use as sire <ArrowRight size={12} />
        </button>
        <button
          type="button"
          data-testid={`bird-use-dam-${bird.id}`}
          onClick={() => handleUseAs("dam")}
          disabled={bird.sex !== "female"}
          className="text-xs border border-border px-2.5 py-1.5 rounded-sm hover:bg-secondary disabled:opacity-30 disabled:cursor-not-allowed inline-flex items-center gap-1 flex-1 justify-center"
        >
          Use as dam <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

const EMPTY_DRAFT = () => ({
  name: "",
  ring_number: "",
  sex: "male",
  hatch_date: "",
  notes: "",
  options: defaultOptions(),
});

function BirdFormDialog({ open, onClose, initial, onSave, saving }) {
  const [draft, setDraft] = useState(initial || EMPTY_DRAFT());
  const [advancedOpen, setAdvancedOpen] = useState(false);

  // Reset draft whenever dialog opens with a different bird
  const dialogKey = initial?.id || "new";
  // useEffect would create extra re-renders; instead key off prop change.
  if ((draft.__key || "") !== dialogKey) {
    setDraft({ ...(initial || EMPTY_DRAFT()), __key: dialogKey });
  }

  const setOptions = (locusId, value) => {
    setDraft((d) => ({
      ...d,
      options: { ...d.options, [locusId]: { state: value } },
    }));
  };

  const handlePhenotype = (cat, opt) => {
    setDraft((d) => applyPhenotype(d, opt, d.sex));
  };

  const handleSave = () => {
    if (!draft.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    const payload = {
      name: draft.name.trim(),
      ring_number: draft.ring_number.trim() || null,
      sex: draft.sex,
      hatch_date: draft.hatch_date.trim() || null,
      notes: draft.notes.trim() || null,
      options: draft.options,
    };
    onSave(payload, initial?.id || null);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="bird-form-dialog"
        className="sm:max-w-3xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle>{initial ? "Edit bird" : "Add a bird"}</DialogTitle>
          <DialogDescription>
            Save your bird&apos;s details and phenotype so you can re-use it in the
            Calculator with a single click.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 py-2">
          <label className="block text-xs uppercase tracking-wider text-muted-foreground">
            Name
            <input
              data-testid="bird-form-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              placeholder="e.g. Bluey"
              maxLength={60}
              className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
            />
          </label>
          <label className="block text-xs uppercase tracking-wider text-muted-foreground">
            Ring number <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span>
            <input
              data-testid="bird-form-ring"
              value={draft.ring_number}
              onChange={(e) => setDraft({ ...draft, ring_number: e.target.value })}
              placeholder="e.g. ABS-2024-1234"
              maxLength={40}
              className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal font-mono"
            />
          </label>

          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground mb-1">Sex</div>
            <div className="flex gap-2">
              {["male", "female"].map((s) => {
                const active = draft.sex === s;
                const activeBg =
                  s === "female"
                    ? "bg-pink-500 text-white border-pink-500"
                    : "bg-primary text-primary-foreground border-primary";
                return (
                  <button
                    key={s}
                    type="button"
                    data-testid={`bird-form-sex-${s}`}
                    onClick={() => setDraft({ ...draft, sex: s })}
                    className={[
                      "px-3 py-2 rounded-sm text-sm border flex-1 transition-colors",
                      active ? activeBg : "border-border hover:bg-secondary",
                    ].join(" ")}
                  >
                    {s === "male" ? "Cock ♂" : "Hen ♀"}
                  </button>
                );
              })}
            </div>
          </div>

          <label className="block text-xs uppercase tracking-wider text-muted-foreground">
            Hatch date <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span>
            <input
              data-testid="bird-form-hatch"
              type="date"
              value={draft.hatch_date}
              onChange={(e) => setDraft({ ...draft, hatch_date: e.target.value })}
              className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
            />
          </label>
        </div>

        <div className="border-t border-border pt-3">
          <div className="text-xs uppercase tracking-wider text-muted-foreground mb-2">
            Phenotype
          </div>
          <div className="divide-y divide-border border border-border rounded-sm">
            {PHENOTYPES.map((cat) => {
              const activeId = findActiveOption(draft, cat);
              return (
                <div key={cat.id} className="px-4 py-3" data-testid={`bird-form-pheno-${cat.id}`}>
                  <div className="text-xs font-medium mb-2">{cat.label}</div>
                  <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-1 px-1">
                    {cat.options.map((opt) => {
                      const active = activeId === opt.id;
                      const ring = draft.sex === "female"
                        ? "border-pink-500 ring-2 ring-pink-500"
                        : "border-primary ring-2 ring-primary";
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          data-testid={`bird-form-pheno-${cat.id}-${opt.id}`}
                          onClick={() => handlePhenotype(cat, opt)}
                          className={[
                            "shrink-0 w-[88px] border rounded-sm overflow-hidden text-left transition-all",
                            active ? ring : "border-border hover:border-foreground/40",
                          ].join(" ")}
                        >
                          <div
                            className="aspect-square"
                            style={{ backgroundColor: opt.swatch }}
                          />
                          <div className="px-1.5 py-1 text-[10px] font-medium leading-tight">
                            {opt.label}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <Collapsible open={advancedOpen} onOpenChange={setAdvancedOpen} className="border border-border rounded-sm mt-3">
          <CollapsibleTrigger className="w-full px-4 py-2 flex items-center justify-between text-left hover:bg-secondary/50">
            <div className="text-xs uppercase tracking-wider text-muted-foreground">Advanced genotype</div>
            <ChevronDown size={14} className={`transition-transform ${advancedOpen ? "rotate-180" : ""}`} />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className="divide-y divide-border border-t border-border">
              {LOCI_UI.map((locus) => {
                const opts =
                  locus.sexLinked && draft.sex === "female"
                    ? locus.options.filter((o) => o.value !== "split")
                    : locus.options;
                return (
                  <div key={locus.id} className="px-4 py-2 grid grid-cols-12 gap-2 items-center">
                    <div className="col-span-12 sm:col-span-5 text-xs font-medium">{locus.label}</div>
                    <div className="col-span-12 sm:col-span-7 flex flex-wrap gap-1">
                      {opts.map((o) => {
                        const active = draft.options[locus.id]?.state === o.value;
                        const activeBg = draft.sex === "female"
                          ? "bg-pink-500 text-white border-pink-500"
                          : "bg-primary text-primary-foreground border-primary";
                        return (
                          <button
                            key={o.value}
                            type="button"
                            data-testid={`bird-form-${locus.id}-${o.value}`}
                            onClick={() => setOptions(locus.id, o.value)}
                            className={[
                              "text-[11px] px-2 py-1 rounded-sm border transition-colors",
                              active ? activeBg : "border-border hover:bg-secondary",
                            ].join(" ")}
                          >
                            {o.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </CollapsibleContent>
        </Collapsible>

        <label className="block text-xs uppercase tracking-wider text-muted-foreground mt-3">
          Notes <span className="lowercase tracking-normal text-muted-foreground/70">(optional)</span>
          <textarea
            data-testid="bird-form-notes"
            value={draft.notes}
            onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
            rows={2}
            maxLength={600}
            placeholder="e.g. Strong show-quality head, English-type."
            className="mt-1 w-full border border-border bg-background px-3 py-2 rounded-sm text-sm normal-case tracking-normal"
          />
        </label>

        <DialogFooter>
          <button
            type="button"
            onClick={onClose}
            className="border border-border px-4 py-2 rounded-sm text-sm hover:bg-secondary"
          >
            Cancel
          </button>
          <button
            data-testid="bird-form-save"
            type="button"
            disabled={saving}
            onClick={handleSave}
            className="bg-foreground text-background px-4 py-2 rounded-sm text-sm font-medium inline-flex items-center gap-2 disabled:opacity-60"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : null}
            {saving ? "Saving…" : initial ? "Update bird" : "Add bird"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Flock() {
  const { isPro, checking } = usePro();
  const { birds, loading, error, limitReached, create, update, remove } = useFlock();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get("tab") === "pairings" ? "pairings" : "birds";
  const setTab = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next === "birds") params.delete("tab");
    else params.set("tab", next);
    setSearchParams(params, { replace: true });
  };
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const openAdd = () => {
    if (limitReached) {
      toast.error(`Free plan saves up to ${FREE_BIRD_LIMIT} birds. Upgrade to Pro for unlimited.`);
      return;
    }
    setEditing(null);
    setDialogOpen(true);
  };

  const openEdit = (bird) => {
    setEditing({
      ...bird,
      ring_number: bird.ring_number || "",
      hatch_date: bird.hatch_date || "",
      notes: bird.notes || "",
    });
    setDialogOpen(true);
  };

  const onSave = async (payload, id) => {
    setSaving(true);
    try {
      if (id) {
        await update(id, payload);
        toast.success(`Updated "${payload.name}"`);
      } else {
        await create(payload);
        toast.success(`Added "${payload.name}" to My Flock`);
      }
      setDialogOpen(false);
    } catch (e) {
      toast.error(e?.message || e?.response?.data?.detail || "Could not save bird.");
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (bird) => {
    if (!window.confirm(`Delete "${bird.name}" from your flock? This cannot be undone.`)) return;
    setDeleting(bird.id);
    try {
      await remove(bird.id);
      toast.success(`Deleted "${bird.name}"`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete bird.");
    } finally {
      setDeleting(null);
    }
  };

  if (checking) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-20 text-center text-muted-foreground">
        <Loader2 className="mx-auto animate-spin mb-3" size={24} />
        Checking your license…
      </div>
    );
  }

  return (
    <div data-testid="flock-page" className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <header className="mb-6 flex items-start justify-between gap-6 flex-wrap">
        <div className="min-w-0">
          <div className="overline">// My Flock</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-black tracking-tight">
            <span className="inline-flex items-center gap-3">
              <Bird size={36} strokeWidth={2} />
              Your Birds
            </span>
          </h1>
          <p className="mt-3 text-muted-foreground max-w-3xl">
            Save your birds with their leg-band ring numbers and full
            phenotype, log every pairing, and track each chick&apos;s ring,
            colour and survival across breeding seasons.
          </p>
          {!isPro && (
            <div
              className="mt-4 inline-flex items-center gap-2 bg-secondary border border-border px-3 py-2 rounded-sm text-xs"
              data-testid="flock-free-banner"
            >
              <Sparkles size={12} />
              <span>
                Free plan saves up to <strong>{FREE_BIRD_LIMIT} birds</strong>.
                {" "}
                <Link to="/pro" className="text-primary underline">
                  Upgrade to Pro
                </Link>
                {" "}for unlimited birds, pair history, and cloud sync.
              </span>
            </div>
          )}
        </div>
        {tab === "birds" && (
          <button
            type="button"
            data-testid="add-bird-btn"
            onClick={openAdd}
            disabled={limitReached}
            className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-sm hover:bg-[hsl(222,89%,42%)] transition-colors font-medium disabled:opacity-50"
          >
            <Plus size={16} /> Add bird
          </button>
        )}
      </header>

      {/* Tabs */}
      <div className="border-b border-border mb-6 flex gap-1" role="tablist">
        {[
          { id: "birds", label: "Birds", icon: Bird, count: birds.length },
          { id: "pairings", label: "Pair history", icon: Heart },
        ].map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              data-testid={`flock-tab-${t.id}`}
              aria-selected={active}
              onClick={() => setTab(t.id)}
              className={[
                "px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors inline-flex items-center gap-2",
                active
                  ? "border-foreground text-foreground"
                  : "border-transparent text-muted-foreground hover:text-foreground",
              ].join(" ")}
            >
              <Icon size={14} />
              {t.label}
              {typeof t.count === "number" && t.count > 0 && (
                <span className="text-[10px] font-mono bg-secondary px-1.5 py-0.5 rounded-sm">
                  {t.count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {tab === "pairings" ? (
        <PairingsTab />
      ) : loading ? (
        <div className="py-20 text-center text-muted-foreground">
          <Loader2 className="mx-auto animate-spin mb-2" size={20} />
          Loading your flock…
        </div>
      ) : error ? (
        <div className="border border-destructive/40 bg-destructive/5 text-destructive p-4 rounded-sm text-sm">
          {error}
        </div>
      ) : birds.length === 0 ? (
        <div
          className="border border-dashed border-border bg-card p-12 text-center"
          data-testid="flock-empty-state"
        >
          <Bird size={36} className="mx-auto text-muted-foreground mb-3" />
          <div className="font-bold text-lg">No birds yet</div>
          <p className="text-muted-foreground text-sm mt-2 max-w-md mx-auto">
            Add your first bird to get started. You can name it, record its
            ring number, and pick its phenotype.
          </p>
          <button
            type="button"
            data-testid="add-first-bird-btn"
            onClick={openAdd}
            className="mt-4 inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-sm font-medium"
          >
            <Plus size={14} /> Add your first bird
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {birds.map((bird) => (
            <BirdCard
              key={bird.id}
              bird={bird}
              onEdit={openEdit}
              onDelete={onDelete}
            />
          ))}
        </div>
      )}

      {deleting && (
        <div className="text-xs text-muted-foreground mt-3">Deleting…</div>
      )}

      <BirdFormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        initial={editing}
        onSave={onSave}
        saving={saving}
      />
    </div>
  );
}
